import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { NavIcon } from '@l2u/ui/components/NavIcon.tsx';

export type ToastVariant = 'success' | 'error';

type ShowToast = (message: string, variant?: ToastVariant) => void;

type Toast = { id: number; message: string; variant: ToastVariant };

const ToastContext = createContext<ShowToast | null>(null);

// Long enough to read a short confirmation without hunting for the dismiss
// button, short enough that the toast is gone before the user reaches for a
// control it might be covering. Applies to successes only — see showToast.
const DISMISS_MS = 3500;

const VARIANT_CLS: Record<ToastVariant, string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  error: 'border-red-200 bg-red-50 text-red-900',
};

// Provides an imperative showToast(): renders a stack of notifications.
// Mounted once near the app root so any screen can `const showToast = useToast()`
// and report an outcome without wiring its own banner state. Sibling of
// ConfirmProvider (lib/confirm.tsx), which uses the same shape for the blocking
// case.
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);
  // Pending dismissals are tracked so unmount can cancel them — a timer firing
  // after teardown would set state on a dead provider.
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(
    () => () => {
      for (const timer of timers.current) clearTimeout(timer);
      timers.current = [];
    },
    [],
  );

  const dismiss = useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback<ShowToast>(
    (message, variant = 'success') => {
      const id = nextId.current++;
      setToasts((ts) => [...ts, { id, message, variant }]);
      // Errors stay until dismissed. A failure that disappears on a timer is
      // easy to miss entirely, and the user still needs the text while deciding
      // what to fix; a success is pure reassurance, so it times out.
      if (variant !== 'error') {
        timers.current.push(setTimeout(() => dismiss(id), DISMISS_MS));
      }
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {/* z-[70] clears both Modal layers (z-50, and z-[60] when elevated). */}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[70] flex w-full max-w-sm flex-col items-end gap-2">
        {/* Two regions, both always mounted and initially empty: assistive tech
            only announces mutations inside a region that already existed, and
            the two need different urgency — a save confirmation must not
            interrupt what the user is doing, a failure should. */}
        <ToastRegion
          role="status"
          ariaLive="polite"
          toasts={toasts.filter((t) => t.variant !== 'error')}
          onDismiss={dismiss}
        />
        <ToastRegion
          role="alert"
          ariaLive="assertive"
          toasts={toasts.filter((t) => t.variant === 'error')}
          onDismiss={dismiss}
        />
      </div>
    </ToastContext.Provider>
  );
}

function ToastRegion({
  role,
  ariaLive,
  toasts,
  onDismiss,
}: {
  role: 'status' | 'alert';
  ariaLive: 'polite' | 'assertive';
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div role={role} aria-live={ariaLive} className="flex w-full flex-col items-end gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast-enter pointer-events-auto flex w-full items-start gap-3 rounded-lg border px-4 py-3 shadow-lg ${VARIANT_CLS[t.variant]}`}
        >
          <span className="flex-1 text-sm font-medium">{t.message}</span>
          <button
            type="button"
            onClick={() => onDismiss(t.id)}
            aria-label="Dismiss notification"
            className="shrink-0 rounded p-0.5 opacity-60 transition-opacity hover:opacity-100"
          >
            <NavIcon name="x" size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function useToast(): ShowToast {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
